import { useState } from 'react'
import { motion } from 'framer-motion'
import { Key, Plus, Trash2, CheckCircle, AlertCircle, Clock, Shield } from 'lucide-react'
import { PageHeader, useAuthStore, fadeUp } from '@personic/shared'
import { buildBreadcrumbs } from '@/config/routes'
import { useApiKeys, useCreateApiKey, useRevokeApiKey, type ApiKey } from '@/hooks/useApiKeys'
import CreateApiKeyModal from '@/components/settings/CreateApiKeyModal'

function StatusBadge({ apiKey }: { apiKey: ApiKey }) {
  if (!apiKey.is_active) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-warm-100 text-warm-600">
        Revoked
      </span>
    )
  }
  if (apiKey.is_expired) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
        <Clock className="w-3 h-3" />
        Expired
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-sage-100 text-sage-700">
      <CheckCircle className="w-3 h-3" />
      Active
    </span>
  )
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export default function ApiKeys() {
  const { user } = useAuthStore()
  const tenantId = user?.tenant_id || ''
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null)
  const [successMsg, setSuccessMsg] = useState('')
  const [errorMsg, setErrorMsg] = useState('')

  const breadcrumbs = buildBreadcrumbs('/settings/integrations/api-keys')
  const { data: keys, isLoading } = useApiKeys(tenantId)
  const createKey = useCreateApiKey(tenantId)
  const revokeKey = useRevokeApiKey(tenantId)

  const activeKeys = keys?.filter(k => k.is_active && !k.is_expired) || []

  const handleCreate = async (data: { name: string; scopes: string[]; expires_days: number | null }) => {
    const result = await createKey.mutateAsync(data)
    setSuccessMsg(`API key "${data.name}" created successfully`)
    setTimeout(() => setSuccessMsg(''), 5000)
    return result
  }

  const handleRevoke = async () => {
    if (!revokeTarget) return
    try {
      await revokeKey.mutateAsync(revokeTarget.id)
      setSuccessMsg(`API key "${revokeTarget.name}" has been revoked`)
      setRevokeTarget(null)
      setTimeout(() => setSuccessMsg(''), 5000)
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.detail || 'Failed to revoke key')
      setRevokeTarget(null)
      setTimeout(() => setErrorMsg(''), 5000)
    }
  }

  return (
    <div>
      <PageHeader
        title="API Keys"
        description="Manage API keys for programmatic access to Personic AI services."
        breadcrumbs={breadcrumbs}
        action={
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-copper-500 text-white text-sm font-medium hover:bg-copper-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Key
          </button>
        }
      />

      {successMsg && (
        <motion.div variants={fadeUp} initial="hidden" animate="visible" className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-sage-50 border border-sage-200 text-sage-700 text-sm max-w-4xl">
          <CheckCircle className="w-4 h-4" />
          {successMsg}
        </motion.div>
      )}

      {errorMsg && (
        <motion.div variants={fadeUp} initial="hidden" animate="visible" className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm max-w-4xl">
          <AlertCircle className="w-4 h-4" />
          {errorMsg}
        </motion.div>
      )}

      <motion.div variants={fadeUp} initial="hidden" animate="visible" className="max-w-4xl">
        <div className="rounded-xl border border-warm-200 bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-copper-50 flex items-center justify-center">
                <Key className="w-5 h-5 text-copper-500" />
              </div>
              <div>
                <h2 className="font-heading font-semibold text-midnight-800">API Keys</h2>
                <p className="text-xs text-warm-500">
                  {activeKeys.length} active key{activeKeys.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-warm-400">
              <Shield className="w-3.5 h-3.5" />
              Keys authenticate via Kong Gateway
            </div>
          </div>

          {isLoading ? (
            <div className="text-sm text-warm-500 text-center py-8">Loading keys...</div>
          ) : !keys?.length ? (
            <div className="text-sm text-warm-500 text-center py-8">
              No API keys yet. Create one to get started with the Personic AI API.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-warm-100">
                    <th className="text-left py-2 pr-4 font-medium text-warm-500">Name</th>
                    <th className="text-left py-2 pr-4 font-medium text-warm-500">Key Prefix</th>
                    <th className="text-left py-2 pr-4 font-medium text-warm-500">Scopes</th>
                    <th className="text-left py-2 pr-4 font-medium text-warm-500">Status</th>
                    <th className="text-left py-2 pr-4 font-medium text-warm-500">Created</th>
                    <th className="text-left py-2 pr-4 font-medium text-warm-500">Last Used</th>
                    <th className="text-right py-2 font-medium text-warm-500"></th>
                  </tr>
                </thead>
                <tbody>
                  {keys.map(apiKey => (
                    <tr key={apiKey.id} className="border-b border-warm-50 last:border-0">
                      <td className="py-3 pr-4">
                        <span className="font-medium text-midnight-800">{apiKey.name}</span>
                      </td>
                      <td className="py-3 pr-4">
                        <code className="text-xs bg-warm-50 px-1.5 py-0.5 rounded font-mono text-warm-600">
                          {apiKey.key_prefix}...
                        </code>
                      </td>
                      <td className="py-3 pr-4">
                        <div className="flex gap-1 flex-wrap">
                          {(apiKey.scopes || []).map(scope => (
                            <span key={scope} className="px-1.5 py-0.5 rounded text-xs bg-warm-50 text-warm-600">
                              {scope}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-3 pr-4">
                        <StatusBadge apiKey={apiKey} />
                      </td>
                      <td className="py-3 pr-4 text-warm-500 text-xs">
                        {formatDate(apiKey.created_at)}
                      </td>
                      <td className="py-3 pr-4 text-warm-500 text-xs">
                        {formatDate(apiKey.last_used_at)}
                      </td>
                      <td className="py-3 text-right">
                        {apiKey.is_active && (
                          <button
                            onClick={() => setRevokeTarget(apiKey)}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-warm-400 hover:text-red-500 transition-colors"
                            title="Revoke key"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </motion.div>

      {/* Create Key Modal */}
      {showCreateModal && (
        <CreateApiKeyModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreate}
          isLoading={createKey.isPending}
        />
      )}

      {/* Revoke Confirmation Dialog */}
      {revokeTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 w-full max-w-sm mx-4 shadow-xl"
          >
            <h3 className="font-heading font-semibold text-midnight-800 mb-2">Revoke API Key</h3>
            <p className="text-sm text-warm-500 mb-1">
              Are you sure you want to revoke <strong className="text-midnight-700">{revokeTarget.name}</strong>?
            </p>
            <p className="text-xs text-red-500 mb-4">
              This action is irreversible. Any services using this key will immediately lose access.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setRevokeTarget(null)}
                className="flex-1 px-4 py-2 rounded-lg border border-warm-200 text-sm font-medium text-warm-600 hover:bg-warm-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRevoke}
                disabled={revokeKey.isPending}
                className="flex-1 px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                {revokeKey.isPending ? 'Revoking...' : 'Revoke Key'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}
